import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import { MapPin, Plus, Edit3, Trash2, X, Download, Upload } from "lucide-react";
import { getAllSfcs, createNewSfc, editSfc, deleteSfc, getAllZones, getAllRoutes, exportSfc, importSfc } from "../../api/master";
import { getAllEmployees } from "../../api/employee";
import { useAuth } from "../../context/AuthContext";

interface Zone {
  id: number;
  name: string;
}

interface Route {
  id: number;
  name: string;
  from_location?: { name: string };
  to_location?: { name: string };
}

const routeLabel = (r: Route) =>
  r.from_location?.name && r.to_location?.name
    ? `${r.from_location.name} to ${r.to_location.name}`
    : r.name;

interface Employee {
  id: number;
  user?: { name: string };
}

interface Sfc {
  id: number;
  zone_id: number;
  route_id: number;
  employee_id: number | null;
  fare: number;
  zone?: Zone;
  route?: Route;
  employee?: Employee;
  updated_at?: string;
}

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const SfcMaster: React.FC = () => {
  const { user } = useAuth();
  const [sfcs, setSfcs] = useState<Sfc[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentSfcId, setCurrentSfcId] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const importInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user?.tenant_id) {
      fetchAll();
    }
  }, [user]);

  const fetchAll = async () => {
    try {
      const [sfcData, zoneData, routeResult, empData] = await Promise.all([
        getAllSfcs(user.tenant_id),
        getAllZones(user.tenant_id),
        getAllRoutes(user.tenant_id, 1, 1000),
        getAllEmployees(user.tenant_id),
      ]);
      setSfcs(sfcData);
      setZones(zoneData);
      setRoutes(routeResult.data);
      setEmployees(empData);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportSfc(user.tenant_id);
    } catch (error) {
      toast.error("Could not download the SFC file.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected after a fix
    e.target.value = "";

    setIsImporting(true);
    try {
      const result = await importSfc(user.tenant_id, file);
      if (result.success) {
        await fetchAll();
        toast.success("SFC records imported successfully.");
      } else {
        toast.error(result.message || "Import failed.");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    } finally {
      setIsImporting(false);
    }
  };

  const sfcSchema = Yup.object({
    zone_id: Yup.number().min(1, "Please select a Zone").required("Zone is required"),
    route_id: Yup.number().min(1, "Please select a Route").required("Route is required"),
    employee_id: Yup.number().nullable(),
    fare: Yup.number().min(0, "Fare must be 0 or more").required("Fare is required"),
  });

  const initialValues = { zone_id: 0, route_id: 0, employee_id: 0, fare: 0 };

  const { setValues, values, handleBlur, handleChange, handleSubmit, errors, touched, resetForm } =
    useFormik({
      initialValues,
      validationSchema: sfcSchema,
      onSubmit: async (vals) => {
        const empId = vals.employee_id > 0 ? vals.employee_id : null;
        try {
          if (!isEditModalOpen) {
            const response = await createNewSfc(
              user.tenant_id,
              vals.zone_id,
              vals.route_id,
              empId,
              vals.fare
            );
            if (response.status === 200) {
              setIsAddModalOpen(false);
              resetForm();
              await fetchAll();
              toast.success("SFC Created Successfully!");
            }
          } else {
            const response = await editSfc(
              currentSfcId,
              user.tenant_id,
              vals.zone_id,
              vals.route_id,
              empId,
              vals.fare
            );
            if (response.status === 200) {
              setIsEditModalOpen(false);
              resetForm();
              await fetchAll();
              toast.success("SFC Updated Successfully!");
            }
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      },
    });

  const handleEdit = (item: Sfc) => {
    setValues({
      zone_id: item.zone_id,
      route_id: item.route_id,
      employee_id: item.employee_id ?? 0,
      fare: item.fare,
    });
    setCurrentSfcId(item.id);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This action cannot be undone.", "Delete SFC record?", "Delete")) return;
    try {
      await deleteSfc(id);
      setSfcs(sfcs.filter((s) => s.id !== id));
      toast.success("SFC record deleted successfully.");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete SFC record.");
    }
  };

  const sfcFormJsx = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Zone *</label>
        <select
          name="zone_id"
          value={values.zone_id}
          onBlur={handleBlur}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={0}>-- Select Zone --</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>{z.name}</option>
          ))}
        </select>
        {errors.zone_id && touched.zone_id && <p className="text-red-500 text-sm">{errors.zone_id}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Route *</label>
        <select
          name="route_id"
          value={values.route_id}
          onBlur={handleBlur}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={0}>-- Select Route --</option>
          {routes.map((r) => (
            <option key={r.id} value={r.id}>{routeLabel(r)}</option>
          ))}
        </select>
        {errors.route_id && touched.route_id && <p className="text-red-500 text-sm">{errors.route_id}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Employee (optional)</label>
        <select
          name="employee_id"
          value={values.employee_id}
          onBlur={handleBlur}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={0}>-- None --</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>{e.user?.name ?? `Employee #${e.id}`}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Fare *</label>
        <input
          type="number"
          name="fare"
          value={values.fare}
          onBlur={handleBlur}
          onChange={handleChange}
          min={0}
          step="0.01"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {errors.fare && touched.fare && <p className="text-red-500 text-sm">{errors.fare}</p>}
      </div>

      <div className="flex justify-end space-x-3 pt-2">
        <button
          type="button"
          onClick={() => { setIsAddModalOpen(false); setIsEditModalOpen(false); resetForm(); }}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {isEditModalOpen ? "Update" : "Add SFC"}
        </button>
      </div>
    </form>
  );

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">SFC Master</h1>
            <p className="text-blue-600 mt-1">Manage Standard Fare Chart</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Hidden file input for import */}
            <input
              ref={importInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleImportChange}
            />
            <button
              onClick={() => importInputRef.current?.click()}
              disabled={isImporting}
              className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              {isImporting ? "Importing…" : "Import Excel"}
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              {isExporting ? "Exporting…" : "Export Excel"}
            </button>
            <button
              onClick={() => { resetForm(); setIsAddModalOpen(true); }}
              className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add SFC
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" /> SFC ({sfcs.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">SN</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Zone</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Route</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Employee</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Fare</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Updated At</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sfcs.map((item, index) => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                      <td className="py-4 px-4 font-medium text-gray-900">{index + 1}</td>
                      <td className="py-4 px-4 text-gray-900">{item.zone?.name ?? "-"}</td>
                      <td className="py-4 px-4 text-gray-900">{item.route ? routeLabel(item.route) : "-"}</td>
                      <td className="py-4 px-4 text-gray-900">{item.employee?.user?.name ?? "-"}</td>
                      <td className="py-4 px-4 text-gray-900">{item.fare}</td>
                      <td className="py-4 px-4 text-gray-900">
                        {item.updated_at ? fmtDate(item.updated_at) : "-"}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sfcs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500">No SFC records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Modal isOpen={isAddModalOpen} onClose={() => { setIsAddModalOpen(false); resetForm(); }} title="Add New SFC">
          {sfcFormJsx}
        </Modal>

        <Modal isOpen={isEditModalOpen} onClose={() => { setIsEditModalOpen(false); resetForm(); }} title="Edit SFC">
          {sfcFormJsx}
        </Modal>
      </div>
    </div>
  );
};

export default SfcMaster;
